import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowRight, House, EnvelopeSimple } from "@phosphor-icons/react";
import { checkoutStatus } from "@/lib/api";
import { LOGO_URL, COMPANY } from "@/lib/site-data";
import { BOOK } from "@/constants/bookIds";

const POLL_DELAYS = [1500, 1500, 2000, 2000, 3000, 3000, 4000, 4000]; // ~21s total

function money(v) {
  if (v == null) return "";
  return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(v);
}

export default function BookSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", payment_status: null, order: null });
  const triesRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: "missing_session" });
      return;
    }
    let cancelled = false;
    async function tick() {
      try {
        const r = await checkoutStatus(sessionId);
        if (cancelled) return;
        setState({ status: r.status, payment_status: r.payment_status, order: r.order, amount_total: r.amount_total, currency: r.currency });
        if (r.payment_status === "paid" || r.status === "expired") return;
        const next = POLL_DELAYS[Math.min(triesRef.current, POLL_DELAYS.length - 1)];
        triesRef.current += 1;
        if (triesRef.current <= POLL_DELAYS.length) {
          setTimeout(tick, next);
        }
      } catch (e) {
        if (!cancelled) {
          setState({ status: "error", payment_status: "error" });
        }
      }
    }
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const expired = state.status === "expired";
  const order = state.order;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b-2 border-slate-900">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Troya" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-black text-base text-slate-900">TROYA <span className="text-sky-500">MINI BINS</span></div>
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-500">Booking</div>
            </div>
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 lg:px-8 py-16 lg:py-20" data-testid={BOOK.payStatus}>
        {paid && order && (
          <div className="border-2 border-emerald-600 bg-white">
            <div className="bg-emerald-600 text-white px-7 py-7 flex items-center gap-4">
              <CheckCircle size={48} weight="fill" />
              <div>
                <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-emerald-100">Confirmed</div>
                <div className="font-display font-black text-2xl sm:text-3xl">Your bin is booked!</div>
              </div>
            </div>
            <div className="p-7 sm:p-10">
              <p className="text-slate-700 leading-relaxed">
                Thanks {order.contact?.name?.split(" ")[0] || "there"} — we&apos;ve received your payment of{" "}
                <span className="font-bold text-slate-900">{money(order.pricing?.total)}</span> for order{" "}
                <span className="font-mono text-sm">#{order.order_id?.slice(0, 8)}</span>.
                A confirmation email is on its way to{" "}
                <span className="font-bold text-slate-900">{order.contact?.email}</span>.
              </p>

              <div className="mt-7 grid sm:grid-cols-2 gap-4 text-sm">
                <div className="border-2 border-slate-900 p-4">
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">Bin</div>
                  <div className="font-display font-bold text-slate-900 mt-1">{order.selection?.category}</div>
                  <div className="text-slate-600">{order.selection?.size}{order.selection?.tons ? ` · up to ${order.selection.tons} ton` : ""}</div>
                </div>
                <div className="border-2 border-slate-900 p-4">
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">Drop-off</div>
                  <div className="font-display font-bold text-slate-900 mt-1">{order.schedule?.dropoff_date}</div>
                  <div className="text-slate-600">{order.schedule?.time_window_label}</div>
                </div>
                <div className="border-2 border-slate-900 p-4">
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">Pickup</div>
                  <div className="font-display font-bold text-slate-900 mt-1">{order.schedule?.pickup_date}</div>
                </div>
                <div className="border-2 border-slate-900 p-4">
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">Delivery to</div>
                  <div className="text-slate-900 mt-1">{order.shipping_address?.line1}, {order.shipping_address?.city}, {order.shipping_address?.province} {order.shipping_address?.postal_code}</div>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-900 text-white font-bold text-xs uppercase tracking-[0.18em] hover:bg-slate-800 transition-colors">
                  <House size={16} weight="bold" /> Back to home
                </Link>
                <a href={`mailto:${COMPANY.email}`} className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-slate-900 text-slate-900 font-bold text-xs uppercase tracking-[0.18em] hover:bg-slate-900 hover:text-white transition-colors">
                  <EnvelopeSimple size={16} weight="bold" /> Email us a question
                </a>
              </div>
            </div>
          </div>
        )}

        {!paid && !expired && state.status !== "error" && (
          <div className="border-2 border-slate-900 bg-white p-10 text-center">
            <div className="inline-flex h-12 w-12 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
            <h2 className="font-display font-black text-2xl text-slate-900 mt-6">Confirming your payment…</h2>
            <p className="text-slate-600 mt-2">This usually takes a few seconds.</p>
          </div>
        )}

        {(expired || state.status === "error") && (
          <div className="border-2 border-rose-600 bg-white p-10 text-center">
            <h2 className="font-display font-black text-2xl text-slate-900">Couldn&apos;t confirm your payment</h2>
            <p className="text-slate-600 mt-3">
              {expired ? "Your checkout session expired before payment completed." : "We hit an issue checking your payment status."}
              {" "}If you were charged, please call us — we&apos;ll sort it out.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Link to="/book" className="inline-flex items-center gap-2 px-6 py-3 bg-sky-500 text-white font-bold text-xs uppercase tracking-[0.18em] hover:bg-sky-600 transition-colors">
                Try again <ArrowRight size={16} weight="bold" />
              </Link>
              <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-900 text-slate-900 font-bold text-xs uppercase tracking-[0.18em]">
                Call us
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
